import { Folder, ListMusic, ListOrdered, Settings2, SearchX } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router';
import { roleAtLeast } from '../../shared/audience';
import type { CollectionDTO, SongListItemDTO } from '../../shared/dto/library';
import { useCollections, useSongs } from '../api/library';
import { useMe } from '../auth/AuthProvider';
import { Page } from '../components/layout';
import { Button, Card, EmptyState } from '../components/ui';
import { useT } from '../i18n';
import { canSaveOffline } from '../offline/hooks';
import { SongOfflineAction } from '../offline/SaveOfflineButton';
import { AllSongsList } from './library/AllSongsList';
import { sortByTitle, sortCollections } from './library/collate';
import { CollectionRow } from './library/CollectionRow';
import { haystack, matchesTokens, queryTokens } from './library/fold';
import { QueryStatus } from './library/QueryStatus';
import { SaveAllCard } from './library/SaveAllCard';
import { SearchField } from './library/SearchField';
import { SegmentedControl } from './library/SegmentedControl';
import { SongRow } from './library/SongRow';
import { PracticeButton } from '../practice/PracticeMenu';

type Tab = 'setlists' | 'folders' | 'all';
const isTab = (v: string | null): v is Tab => v === 'setlists' || v === 'folders' || v === 'all';
const songHref = (id: string) => `/songs/${id}`;

type RowAction = ((song: SongListItemDTO) => ReactNode) | undefined;

/** Per-row "Vista fyrir ónettengt" action (absent where saving is not offered, e.g. iOS Safari tab). */
function offlineRowAction(): RowAction {
  return canSaveOffline() ? (s) => <SongOfflineAction songId={s.id} title={s.title} /> : undefined;
}

/** Number of entries the viewer can open (entries for songs they can't see are not counted). */
function visibleCount(c: CollectionDTO, visible: Set<string> | null): number {
  return visible ? c.entries.filter((e) => visible.has(e.songId)).length : c.entries.length;
}

function CollectionList({ items, visible, kind }: { items: CollectionDTO[]; visible: Set<string> | null; kind: 'setlist' | 'folder' }) {
  const t = useT();
  if (items.length === 0) {
    return (
      <EmptyState icon={kind === 'setlist' ? ListOrdered : Folder} title={t(kind === 'setlist' ? 'library.empty_setlists' : 'library.empty_folders')}>
        {t('library.empty_collections_hint')}
      </EmptyState>
    );
  }
  return (
    <Card className="overflow-hidden">
      <ul className="divide-y divide-[var(--line)]">
        {items.map((c) => (
          <CollectionRow key={c.id} collection={c} to={`/collections/${c.id}`} count={visibleCount(c, visible)} />
        ))}
      </ul>
    </Card>
  );
}

function SearchResults({
  q,
  songs,
  collections,
  visible,
  rowAction,
}: {
  q: string;
  songs: SongListItemDTO[];
  collections: CollectionDTO[];
  visible: Set<string> | null;
  rowAction: RowAction;
}) {
  const t = useT();
  const tokens = useMemo(() => queryTokens(q), [q]);
  const indexed = useMemo(() => sortByTitle(songs).map((s) => ({ s, hay: haystack(s.title, s.composer, s.arranger) })), [songs]);
  const songHits = useMemo(() => indexed.filter((x) => matchesTokens(x.hay, tokens)).map((x) => x.s), [indexed, tokens]);
  const colHits = useMemo(() => collections.filter((c) => matchesTokens(haystack(c.name, c.description), tokens)), [collections, tokens]);

  if (songHits.length === 0 && colHits.length === 0) {
    return <EmptyState icon={SearchX} title={t('library.search_none', { q: q.trim() })}>{t('library.search_none_hint')}</EmptyState>;
  }
  return (
    <div className="flex flex-col gap-5">
      {colHits.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h2 className="px-1 text-[15px] font-bold text-[var(--ink-2)]">{t('library.search_collections')}</h2>
          <Card className="overflow-hidden">
            <ul className="divide-y divide-[var(--line)]">
              {colHits.map((c) => (
                <CollectionRow key={c.id} collection={c} to={`/collections/${c.id}`} count={visibleCount(c, visible)} />
              ))}
            </ul>
          </Card>
        </section>
      ) : null}
      {songHits.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h2 className="px-1 text-[15px] font-bold text-[var(--ink-2)]" aria-live="polite">
            {t('library.search_results', { count: songHits.length })}
          </h2>
          <Card className="overflow-hidden">
            <ul className="divide-y divide-[var(--line)]">
              {songHits.map((s) => (
                <SongRow key={s.id} song={s} to={songHref(s.id)} action={rowAction?.(s)} />
              ))}
            </ul>
          </Card>
        </section>
      ) : null}
    </div>
  );
}

/** /songs — setlists, folders and all songs A–Ö, with accent-folded search. */
export default function SongsScreen() {
  const t = useT();
  const me = useMe();
  const navigate = useNavigate();
  const isEditor = !!me && roleAtLeast(me.role, 'editor');
  const songsQ = useSongs();
  const colsQ = useCollections();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigationType = useNavigationType();
  const urlQ = params.get('q') ?? '';
  // The field is local state (the URL update is async, so typing must not wait for it) and is
  // written to ?q= with replace. Any other navigation to this screen while it stays mounted — the
  // "Lög" tab tapped from /songs?q=…, Back/Forward — is a PUSH/POP: the URL wins then.
  const [q, setQ] = useState(urlQ);
  const [seenKey, setSeenKey] = useState(location.key);
  if (location.key !== seenKey) {
    setSeenKey(location.key);
    if (navigationType !== 'REPLACE' && urlQ !== q) setQ(urlQ);
  }

  const setParam = (key: string, value: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  const collections = useMemo(() => sortCollections(colsQ.data ?? []), [colsQ.data]);
  const setlists = useMemo(() => collections.filter((c) => c.kind === 'setlist'), [collections]);
  const folders = useMemo(() => collections.filter((c) => c.kind === 'folder'), [collections]);
  const visible = useMemo(() => (songsQ.data ? new Set(songsQ.data.map((s) => s.id)) : null), [songsQ.data]);

  const tabParam = params.get('tab');
  const tab: Tab = isTab(tabParam) ? tabParam : colsQ.data && setlists.length === 0 ? 'all' : 'setlists';
  const searching = queryTokens(q).length > 0;
  const rowAction = offlineRowAction();

  let content;
  if (searching) {
    content = songsQ.data ? (
      <SearchResults q={q} songs={songsQ.data} collections={collections} visible={visible} rowAction={rowAction} />
    ) : (
      <QueryStatus q={songsQ} />
    );
  } else if (tab === 'all') {
    content = songsQ.data ? (
      <div className="flex flex-col gap-4">
        {songsQ.data.length > 0 ? <SaveAllCard /> : null}
        <AllSongsList songs={songsQ.data} songHref={songHref} rowAction={rowAction} />
      </div>
    ) : (
      <QueryStatus q={songsQ} />
    );
  } else {
    content = colsQ.data ? (
      <CollectionList items={tab === 'setlists' ? setlists : folders} visible={visible} kind={tab === 'setlists' ? 'setlist' : 'folder'} />
    ) : (
      <QueryStatus q={colsQ} />
    );
  }

  return (
    <Page
      title={t('library.title')}
      actions={
        <>
          <PracticeButton />
          {isEditor ? (
            <Button variant="secondary" icon={Settings2} onClick={() => navigate('/admin/library')} aria-label={t('library.manage')}>
              <span className="hidden sm:inline">{t('library.manage')}</span>
            </Button>
          ) : null}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <SearchField
          value={q}
          onChange={(v) => {
            setQ(v);
            setParam('q', v.trim() ? v : null);
          }}
          placeholder={t('library.search_placeholder')}
        />
        {!searching ? (
          <SegmentedControl<Tab>
            label={t('library.title')}
            value={tab}
            onChange={(v) => setParam('tab', v)}
            options={[
              { value: 'setlists', label: t('library.tab_setlists'), icon: ListOrdered },
              { value: 'folders', label: t('library.tab_folders'), icon: Folder },
              { value: 'all', label: t('library.tab_all'), icon: ListMusic },
            ]}
          />
        ) : null}
        {content}
      </div>
    </Page>
  );
}
